import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Flex, Group, Separator, Text, VStack } from "@chakra-ui/react";

import ErrorBoundaryContent from "@components/error-boundary/error-boundary-content";
import { Button } from "@components/ui/button";
import { StepsContent, StepsItem, StepsList, StepsRoot } from "@components/ui/steps";

import MappingsPage from "@compositions/mappings";

import { useUpdateStageMutation } from "@state/apiSlice/requests";
import { selectMondayContextData, selectMondayIsAdmin, selectMondayState } from "@state/mondaySlice";

import GoogleConnectionStep from "./google-connect-step";
import MondayConnectionStep from "./monday-connection-step";
import PermissionsStep from "./permissions-step";
import SetupCompleteStage from "./setup-complete-step";

const OnboardingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminUser = useSelector(selectMondayIsAdmin);
  const { isLoading, init, boardId, itemId } = useSelector(selectMondayState);

  console.log(`boardId is ${boardId}, itemId is ${itemId}`);

  const { stage, connections, mapping, merchantId: currentMerchantId } = init;
  const { hasMapping, entityType } = mapping;
  const { isMondayConnected, isGoogleConnected } = connections;

  const isFullyConnected = isMondayConnected && isGoogleConnected;

  const [updateStage] = useUpdateStageMutation();

  // Parse reconnection flags from query
  const { reconnectMonday, reconnectGoogle, reconnectBoth } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      reconnectMonday: query.get("reconnectMonday") === "true",
      reconnectGoogle: query.get("reconnectGoogle") === "true",
      reconnectBoth: query.get("reconnectBoth") === "true",
    };
  }, [location.search]);

  const isReconnectFlow = reconnectMonday || reconnectGoogle || reconnectBoth;

  const shouldShowOnlyConnectionsStep = !isAdminUser || isReconnectFlow;

  const initialStep = reconnectGoogle || reconnectMonday || reconnectBoth ? 0 : !isFullyConnected ? 0 : 1;
  const [step, setStep] = useState(initialStep);

  useEffect(() => {
    if (!isLoading && !isReconnectFlow && stage === "COMPLETE" && hasMapping && isFullyConnected) {
      navigate("/", { replace: true });
    }
  }, [isLoading, isReconnectFlow, stage, hasMapping, isFullyConnected, navigate]);

  useEffect(() => {
    if (!isLoading && !isReconnectFlow && isAdminUser) {
      setStep(isFullyConnected ? 1 : 0);
    }
  }, [isLoading, isReconnectFlow, isAdminUser, isFullyConnected]);

  useEffect(() => {
    const color = "#f4f4f5";
    document.body.style.backgroundColor = color;
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  if (!isAdminUser && stage !== "COMPLETE") {
    return <ErrorBoundaryContent type="BOARD_NOT_CONFIGURED" />;
  }

  const onSaveMappingsCallback = () => {
    if (!boardId || !itemId) {
      return null;
    }

    updateStage({ boardId, itemId }).then(() => setStep(3));
  };

  const MONDAY_CONNECTION_STEP = {
    title: "Monday Connection",
    content: (
      <MondayConnectionStep
        stage={stage}
        setStep={setStep}
        data={{ isMondayConnected }}
        isLoading={isLoading}
        isMondayConnected={isMondayConnected}
      />
    ),
  };

  const GOOGLE_CONNECTION_STEP = {
    title: "Google Connection",
    content: (
      <GoogleConnectionStep
        stage={stage}
        setStep={setStep}
        currentMerchantId={currentMerchantId}
        data={{ isGoogleConnected, isMondayConnected }}
        isLoading={isLoading}
        isMondayConnected={isMondayConnected}
      />
    ),
  };

  const ADMIN_STEPS = [
    MONDAY_CONNECTION_STEP,
    GOOGLE_CONNECTION_STEP,
    {
      title: "Mappings",
      content: (
        <MappingsPage
          isOnboarding
          onSaveCallback={onSaveMappingsCallback}
          onGoBackHandler={() => setStep(1)}
        />
      ),
    },
  ];

  let steps = ADMIN_STEPS;

  if (shouldShowOnlyConnectionsStep) {
    if (reconnectBoth) {
      steps = [MONDAY_CONNECTION_STEP, GOOGLE_CONNECTION_STEP];
    } else if (reconnectGoogle) {
      steps = [GOOGLE_CONNECTION_STEP];
    } else if (reconnectMonday) {
      steps = [MONDAY_CONNECTION_STEP];
    }
  }

  return (
    <Flex
      flexDir="column"
      alignItems="center"
      justifyContent="center"
      p="15px"
      bg="gray.100"
    >
      <Flex
        direction="column"
        flex="1"
        maxW="5xl"
      >
        <Box
          textAlign="center"
          w="full"
        >
          <Text
            fontSize="xl"
            fontWeight="bold"
          >
            Setup
          </Text>
          <Text
            fontSize="md"
            color="gray.600"
          >
            Seamlessly integrate your GMC account with monday and get started in no time.
          </Text>
          <Separator my="24px" />
        </Box>

        <StepsRoot
          step={step}
          count={steps.length}
          alignItems="center"
          flex="1"
        >
          <StepsList
            w="full"
            alignItems="center"
            justifyContent="center"
          >
            {steps.map((s, index) => (
              <StepsItem
                key={index}
                index={index}
                // title={index === step ? s.title : undefined}
              />
            ))}
          </StepsList>

          {step !== steps.length && (
            <StepsContent
              index={step}
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="full"
            >
              <VStack
                gap={0}
                bg="white"
                p={6}
                borderRadius="lg"
                shadow="xs"
                align="flex-start"
                w={{ base: "full", lg: "752px" }}
                maxW="752px"
              >
                {steps[step]?.content}
              </VStack>
            </StepsContent>
          )}

          {step === steps.length && <SetupCompleteStage />}

          {import.meta.env.MODE !== "production" && (
            <Group
              w="full"
              justifyContent="flex-end"
            >
              {step > 0 && (
                <Button
                  size="sm"
                  onClick={() => setStep((prev) => prev - 1)}
                >
                  Prev (debug)
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setStep((prev) => prev + 1)}
                disabled={step >= steps.length}
              >
                Next (debug)
              </Button>
            </Group>
          )}
        </StepsRoot>
      </Flex>
    </Flex>
  );
};

export default OnboardingPage;
